import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { playersApi } from '../api/client';
import SEO, { SITE_URL } from '../components/SEO';

export default function PlayerDetail() {
  const { id } = useParams();
  const [player, setPlayer] = useState(null);

  useEffect(() => { playersApi.detail(id).then(r => setPlayer(r.data)); }, [id]);

  if (!player) return <div className="container" style={{ padding: '4rem 0', fontFamily: 'sans-serif', color: '#999' }}>Loading…</div>;

  const initials = player.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  const playerJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: player.full_name,
    description: player.bio || `${player.full_name} — ${player.position} · ${player.nationality}`,
    url: `${SITE_URL}/players/${player.id}`,
    ...(player.profile_photo && { image: player.profile_photo }),
    ...(player.nationality && { nationality: { '@type': 'Country', name: player.nationality } }),
    ...(player.current_club && { affiliation: { '@type': 'SportsTeam', name: player.current_club } }),
    jobTitle: player.position,
  };

  return (
    <div style={{ padding: '3rem 0' }}>
      <SEO
        title={`${player.full_name} — ${player.position}`}
        description={
          player.bio
            ? player.bio.slice(0, 155)
            : `${player.full_name} (${player.position}) from ${player.nationality || 'Africa'} — view profile on Pitch & Pen.`
        }
        image={player.profile_photo}
        url={`${SITE_URL}/players/${player.id}`}
        type="profile"
        jsonLd={playerJsonLd}
      />
      <div className="container">
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', marginBottom: '2rem', flexWrap: 'wrap' }}>
            <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#e8f5ee', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif', fontWeight: 700, color: '#1a6b45', fontSize: '1.5rem', flexShrink: 0 }}>
              {player.profile_photo ? <img src={player.profile_photo} alt={player.full_name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : initials}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <h1 style={{ fontSize: '1.75rem' }}>{player.full_name}</h1>
                {player.is_verified && <span style={{ background: '#e8f5ee', color: '#1a6b45', padding: '2px 8px', borderRadius: 20, fontSize: '0.75rem', fontFamily: 'sans-serif' }}>✓ Verified</span>}
              </div>
              <p style={{ fontFamily: 'sans-serif', color: '#666', marginTop: '0.25rem' }}>{player.position} · {player.nationality}</p>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                <span className={`badge badge-${player.status === 'active' ? 'green' : player.status === 'seeking' ? 'amber' : ''}`}
                  style={player.status === 'retired' ? { background: '#f5f5f5', color: '#666', padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem', fontFamily: 'sans-serif' } : {}}>
                  {player.status}
                </span>
                {player.age && <span className="badge" style={{ background: '#f5f5f5', color: '#666' }}>Age {player.age}</span>}
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
            {[
              ['Current club', player.current_club || '—'],
              ['Height', player.height_cm ? `${player.height_cm} cm` : '—'],
              ['Preferred foot', player.preferred_foot || '—'],
            ].map(([label, val]) => (
              <div key={label} style={{ background: '#f9f9f7', borderRadius: 8, padding: '1rem' }}>
                <div style={{ fontFamily: 'sans-serif', fontSize: '0.75rem', color: '#999', marginBottom: 4 }}>{label}</div>
                <div style={{ fontFamily: 'sans-serif', fontWeight: 600, fontSize: '0.95rem' }}>{val}</div>
              </div>
            ))}
          </div>
          {player.bio && <div style={{ marginBottom: '2rem' }}><h3 style={{ marginBottom: '0.75rem' }}>About</h3><p style={{ lineHeight: 1.8, color: '#333' }}>{player.bio}</p></div>}
          {player.previous_clubs_list?.length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ marginBottom: '0.75rem' }}>Previous clubs</h3>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {player.previous_clubs_list.map(c => <span key={c} style={{ background: '#f5f5f5', padding: '4px 12px', borderRadius: 20, fontFamily: 'sans-serif', fontSize: '0.85rem', color: '#444' }}>{c}</span>)}
              </div>
            </div>
          )}
          {player.video_url && (
            <a href={player.video_url} target="_blank" rel="noreferrer" className="btn btn-outline" style={{ fontFamily: 'sans-serif' }}>Watch highlights →</a>
          )}
        </div>
      </div>
    </div>
  );
}
